import { Debug } from "@src/utils/debug";
import { DataCreator } from "../../../utils/dataCreator";
import { Utilities } from "../../../utils/utilities";
import { FetchStatistics, FirebaseData, FirebaseRealtimeLister } from "./FirebaseData";
import {
  CollectionReference,
  FirestoreError,
  Query,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  onSnapshot,
  setDoc,
  updateDoc,
  writeBatch,
} from "firebase/firestore";

interface NoParamConstructor<T extends FirebaseData> {
  new (id: string, collection: string, parent: FirebaseData | undefined): T;
}

export class CollectionLoadedListener<T extends FirebaseData> {
  id = Utilities.generateUUID();
  loader: CollectionLoader<T>;
  callback: (docs: T[]) => void;
  constructor(callback: (docs: T[]) => void, loader: CollectionLoader<T>) {
    this.callback = callback;
    this.loader = loader;
  }
  removeListener() {
    this.loader.removeListener(this);
  }
}

export default class CollectionLoader<T extends FirebaseData> {
  collection: string = "";
  parent?: FirebaseData;
  loaded = false;
  loading = false;
  loadedListeners: CollectionLoadedListener<T>[] = [];
  documents: T[] = [];
  realtimeListeners: (() => void)[] = [];
  fetchCount: number = 0;
  private docType: NoParamConstructor<T>;

  constructor(collection: string, parent: FirebaseData | undefined, docType: NoParamConstructor<T>) {
    this.collection = collection;
    this.parent = parent;
    this.docType = docType;
  }

  load(completion: (documents: T[]) => void) {
    const db = getFirestore();
    this.loadedListeners.push(new CollectionLoadedListener(completion, this));
    const path = this.getPath();

    const collectionRef = collection(db, path);

    if (!this.loading) {
      this.loading = true;
      this.fetchCount++;
      FetchStatistics.fetchCount++;
      Debug.print(`[${path}] Fetching ${this.fetchCount} / ${FetchStatistics.fetchCount}`);
      getDocs(collectionRef)
        .then((snapshot) => {
          this.documents = [];

          if (!this.parent?.deleted) {
            const docs = snapshot?.docs.filter((doc) => this.collection === "trashcan" || !doc.data().deletedBy) ?? [];

            for (const document of docs) {
              let data = this.addData(document.id);
              data.setup(document.data());
            }
          }
        })
        .catch((error) => {
          console.error("Error loading documents from " + path);
          console.error(error.message);
        })
        .finally(() => {
          this.loaded = true;
          this.loading = false;

          for (const listener of this.loadedListeners) {
            listener.callback(this.documents);
          }

          this.loadedListeners = [];
        });
    }
  }
  loadWithPromise(): Promise<T[]> {
    const db = getFirestore();
    const path = this.getPath();

    if (this.collection === "trashcan" || this.parent?.deleted) return Promise.resolve([]);

    const collectionRef = collection(db, path);
    this.loading = true;
    this.fetchCount++;
    FetchStatistics.fetchCount++;
    Debug.print(`[${path}] Fetching ${this.fetchCount} / ${FetchStatistics.fetchCount}`);
    return getDocs(collectionRef)
      .then((snapshot) => {
        this.documents = [];
        const docs = snapshot?.docs.filter((doc) => this.collection === "trashcan" || !doc.data().deletedBy) ?? [];

        for (const document of docs) {
          let data = this.addData(document.id);
          data.setup(document.data());
        }
        return this.documents;
      })
      .catch((error) => {
        console.error("Error loading documents from " + path);
        console.error(error.message);
        throw error;
      })
      .finally(() => {
        this.loaded = true;
        this.loading = false;

        for (const listener of this.loadedListeners) {
          listener.callback(this.documents);
        }

        this.loadedListeners = [];
        return this.documents;
      });
  }

  loadWithFilter(refToFilter: (ref: CollectionReference) => Query, completion: (documents: T[]) => void) {
    const db = getFirestore();

    const path = this.getPath();
    const newDocuments: T[] = [];

    if (this.collection === "trashcan" || this.parent?.deleted) {
      completion([]);
      return;
    }

    this.fetchCount++;
    FetchStatistics.fetchCount++;
    Debug.print(`[${path}] Fetching ${this.fetchCount} / ${FetchStatistics.fetchCount}`);
    const filterRef = refToFilter(collection(db, path));
    getDocs(filterRef)
      .then((snapshot) => {
        const docs = snapshot?.docs.filter((doc) => this.collection === "trashcan" || !doc.data().deletedBy) ?? [];

        for (const document of docs) {
          let data = this.addData(document.id);
          data.setup(document.data());
          newDocuments.push(data);
        }
      })
      .catch((error: FirestoreError) => {
        console.error("Error loading documents from " + path);
        console.error(error.message);
      })
      .finally(() => {
        this.loaded = true;
        this.loading = false;

        completion(newDocuments);
      });
  }
  loadDocuments(ids: string[], completion: (documents: T[]) => void) {
    const path = this.getPath();
    const db = getFirestore();

    this.fetchCount++;
    FetchStatistics.fetchCount++;
    Debug.print(`[${path}] Fetching ${this.fetchCount} / ${FetchStatistics.fetchCount}`);
    this.loadedListeners.push(new CollectionLoadedListener(completion, this));
    this.loading = true;
    return Promise.all(
      ids.map((id) => {
        const docRef = doc(db, path, id);
        return getDoc(docRef).then((snapshot) => {
          const docData = snapshot.data();
          if (docData && (!docData.deletedBy || this.collection === "trashcan")) {
            const doc = this.addData(snapshot.id);
            doc.setup(docData);
          }
        });
      })
    ).finally(() => {
      this.loaded = true;
      this.loading = false;

      for (const listener of this.loadedListeners) {
        listener.callback(this.documents);
      }

      this.loadedListeners = [];
    });
  }

  loadDocument(id: string, completion: ((document: T | undefined) => void) | undefined) {
    let document: T;
    const path = this.getPath();
    const db = getFirestore();
    const docRef = doc(db, path, id);

    this.fetchCount++;
    FetchStatistics.fetchCount++;
    Debug.print(`[${path}] Fetching ${this.fetchCount} / ${FetchStatistics.fetchCount}`);
    getDoc(docRef)
      .then((snapshot) => {
        if (snapshot && snapshot.exists()) {
          const data = snapshot.data();
          document = this.addData(snapshot.id);
          document.setup(data);
        }
      })
      .catch((error) => {
        console.error("Error loading document " + id + " from " + path);
        console.error(error.message);
      })
      .finally(() => {
        completion?.(document);
      });
  }
  loadDocumentWithPromise(id: string): Promise<T> {
    const path = this.getPath();
    const db = getFirestore();
    const docRef = doc(db, path, id);

    return getDoc(docRef)
      .then((snapshot) => {
        if (snapshot && snapshot.exists()) {
          const data = snapshot.data();
          const document = this.addData(snapshot.id);
          document.setup(data);
          return document;
        }
        throw new Error("Document not found");
      })
      .catch((error) => {
        console.error("Error loading document " + id + " from " + path);
        console.error(error.message);
        throw error;
      });
  }
  setupRealtimeListenerForDocument(id: string, completion: (document: T | undefined) => void): FirebaseRealtimeLister {
    let document: T;
    const path = this.getPath();

    const db = getFirestore();
    const docRef = doc(db, path, id);

    this.fetchCount++;
    FetchStatistics.fetchCount++;
    Debug.print(`[${path}] Fetching ${this.fetchCount} / ${FetchStatistics.fetchCount}`);
    const listener = new FirebaseRealtimeLister(
      onSnapshot(
        docRef,
        (doc) => {
          const data = doc.data();
          document = this.addData(doc.id);
          if (data) document.setup(data);
          completion(document);
        },
        (error) => {
          this.removeData(id);
          console.error(error);
          completion(undefined);
        }
      )
    );

    return listener;
  }

  updateDocument(id: string, data: { [key: string]: any }, completion: (error?: Error) => void) {
    const path = this.getPath();
    const db = getFirestore();
    const docRef = doc(db, path, id);

    updateDoc(docRef, {
      updatedBy: DataCreator.getUpdatedBy(),
      ...data,
    })
      .then((snapshot) => {
        completion(undefined);
      })
      .catch((error) => {
        console.error("Error updating document " + id + " on " + path);
        console.error(error.message);
        completion(error);
      });
  }
  updateDocumentWithPromise(id: string, data: { [key: string]: any }): Promise<T> {
    const path = this.getPath();
    const db = getFirestore();
    const docRef = doc(db, path, id);

    return updateDoc(docRef, {
      updatedBy: DataCreator.getUpdatedBy(),
      ...data,
    })
      .then(() => {
        return this.loadDocumentWithPromise(id);
      })
      .catch((error) => {
        console.error("Error updating document " + id + " on " + path);
        console.error(error.message);
        throw error;
      });
  }
  deleteDocument(id: string): Promise<any> {
    const path = this.getPath();
    const db = getFirestore();
    const ref = doc(db, path, id);
    return deleteDoc(ref)
      .then(() => {
        const data = this.getData(id);
        if (data) data.deleted = true;
        this.documents = this.documents.filter((doc) => doc.id !== id);
      })
      .catch((error) => {
        console.error("Error updating document " + id + " on " + path);
        console.error(error.debugDescription);
        throw error;
      });
  }
  deleteDocumentWithDeletedBy(id: string): Promise<any> {
    return updateDoc(doc(getFirestore(), this.getPath(), id), {
      deletedBy: DataCreator.getDeletedBy(),
    }).then(() => {
      const data = this.getData(id);
      if (data) data.deleted = true;
      this.documents = this.documents.filter((doc) => doc.id !== id);
      return data;
    });
  }
  deleteAllDocuments(): Promise<any> {
    const db = getFirestore();
    const batch = writeBatch(db);
    const batches = [batch];
    let docCount = 0;
    this.documents.forEach((document) => {
      const docRef = doc(db, document.getCompletePath());
      if (docCount >= 499) {
        batches.push(batch);
        docCount = 0;
      }
      batches[batches.length - 1].delete(docRef);
      docCount++;
    });
    return Promise.all(
      batches.map((batch) =>
        batch
          .commit()
          .then(() => {
            this.documents = [];
          })
          .catch((error) => {
            console.error("Error deleting documents");
            console.error(error.debugDescription);
            throw error;
          })
      )
    );
  }

  setupRealtimeListenerWithFilter(refToFilter: (ref: CollectionReference) => Query, onUpdate: (docs: T[]) => void) {
    const db = getFirestore();
    const path = this.getPath();
    const listener = new CollectionLoadedListener<T>(onUpdate, this);
    this.fetchCount++;
    FetchStatistics.fetchCount++;
    Debug.print(`[${path}] Fetching ${this.fetchCount} / ${FetchStatistics.fetchCount}`);
    this.realtimeListeners.push(
      onSnapshot(
        refToFilter(collection(db, path)),
        (query) => {
          const newDocs: T[] = [];
          for (const doc of query.docChanges()) {
            if (doc.type === "removed") {
              this.removeData(doc.doc.id);
            }
          }
          this.loaded = true;

          const docs = query.docs.filter((doc) => this.collection === "trashcan" || !doc.data().deletedBy);

          for (const doc of docs) {
            const data = this.addData(doc.id);
            data.setup(doc.data());
            newDocs.push(data);
          }
          onUpdate(newDocs);
        },
        (error) => {
          console.error("Error setting up realtime listener at " + path);
          console.error(error.message);
          this.loaded = true;
          onUpdate([]);
        }
      )
    );
    return listener;
  }

  setupRealtimeListener(onUpdate: (docs: T[]) => void) {
    const db = getFirestore();
    const path = this.getPath();
    const listener: CollectionLoadedListener<T> = new CollectionLoadedListener(onUpdate, this);
    this.loadedListeners.push(listener);

    this.fetchCount++;
    FetchStatistics.fetchCount++;
    Debug.print(`[${path}] Fetching ${this.fetchCount} / ${FetchStatistics.fetchCount}`);
    if (this.realtimeListeners.length === 0) {
      this.realtimeListeners.push(
        onSnapshot(
          collection(db, path),
          (query) => {
            // Removed docs
            for (const doc of query.docChanges()) {
              if (doc.type === "removed") {
                this.removeData(doc.doc.id);
              }
            }

            // Added/updated
            const docs = query.docs.filter((doc) => this.collection === "trashcan" || !doc.data().deletedBy);

            for (const doc of docs) {
              const docData = doc.data();
              const data = this.addData(doc.id);
              data.setup(docData);
            }

            this.loaded = true;
            this.loadedListeners.forEach((listener) => {
              listener.callback(this.documents);
            });
          },
          (error) => {
            console.error("Error setting up realtime listener for " + path);
            console.error(error.message);
            this.loaded = true;
            this.loadedListeners.forEach((listener) => {
              listener.callback(this.documents);
            });
          }
        )
      );
    }

    return listener;
  }
  removeAllRealtimeListeners() {
    for (const listener of this.realtimeListeners) {
      listener();
    }
    this.loadedListeners = [];
    this.realtimeListeners = [];
  }
  removeListener(listener?: CollectionLoadedListener<T>) {
    this.loadedListeners = this.loadedListeners.filter((list) => list.id !== listener?.id);

    if (this.loadedListeners.length === 0) {
      this.removeAllRealtimeListeners();
    }
  }

  removeData(id: string) {
    this.documents = this.documents.filter((doc) => doc.id !== id);
  }

  remove(data?: T) {
    this.documents = this.documents.filter((doc) => doc !== data);
  }

  createData(data: { [key: string]: any }, id?: string): Promise<T> {
    const db = getFirestore();
    const ref = doc(db, this.getPath(), id ?? this.generateID());

    if (!("createdBy" in data) || Object.keys(data.createdBy).length === 0) {
      data.createdBy = DataCreator.getCreatedBy();
    }

    return setDoc(ref, data)
      .then(() => {
        const document = this.addData(ref.id);
        document.setup(data);
        return document;
      })
      .catch((error) => {
        console.error("Error creating " + ref.path + ": " + error);
        FirebaseData.saveErrorLog("set", ref.path, data, error);
        throw error;
      });
  }

  createDataWithMerge(id: string | undefined, data: { [key: string]: any }): Promise<T> {
    const db = getFirestore();
    const ref = doc(db, this.getPath(), id ?? this.generateID());

    return setDoc(
      ref,
      {
        ...data,
        updatedBy: DataCreator.getUpdatedBy(),
      },
      { merge: true }
    )
      .then(() => {
        const document = this.addData(ref.id);
        document.setup(data);
        return document;
      })
      .catch((error) => {
        console.error("Error creating " + ref.id + ": " + error);
        throw error;
      });
  }

  async duplicateDataToCollection<T extends FirebaseData>(collectionLoader: CollectionLoader<T>) {
    const promises: Promise<any>[] = [];

    if (!this.loaded) {
      await this.loadWithPromise();
    }

    for (const doc of this.documents) {
      collectionLoader.createData(
        {
          createdBy: DataCreator.getCreatedBy(),
          ...doc.rawData,
        },
        doc.id
      );
    }
    return Promise.all(promises);
  }

  addData(id: string): T {
    const existing = this.documents.find((doc) => doc.id === id);
    if (existing) {
      return existing;
    }

    const document = new this.docType(id, this.collection, this.parent);
    document.parent = this.parent;
    this.documents.push(document);
    return document;
  }

  generateTemporaryData() {
    const document = new this.docType(this.generateID(), this.collection, this.parent);
    document.parent = this.parent;
    return document;
  }

  getData(id: string | undefined): T | undefined {
    return this.documents.find((doc) => doc.id === id);
  }

  hasData(id: string | undefined): boolean {
    return this.documents.find((doc) => doc.id === id) !== undefined;
  }

  generateID(): string {
    const db = getFirestore();
    return doc(collection(db, this.getPath())).id;
  }

  getPath() {
    return this.parent ? this.parent.getCompletePath() + "/" + this.collection : this.collection;
  }

  size() {
    return this.documents.length;
  }
}
